import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AgentPlatformMenuIcon } from '../AgentPlatform/AgentPlatformMenuIcon';
import { AGENT_PLATFORM_BASE } from '../../constants/agentPlatformRoutes';
import type { agentPlatformMenuIconPaths } from '../../constants/agentPlatformMenuIcons';

type IconKey = keyof typeof agentPlatformMenuIconPaths | 'agents';

type MenuItem = {
  label: string;
  path: string;
  icon: IconKey;
  description?: string;
};

const menuItems: MenuItem[] = [
  {
    label: 'Início',
    path: AGENT_PLATFORM_BASE,
    icon: 'home',
    description: 'Hub da plataforma',
  },
  {
    label: 'Assistente de IA',
    path: `${AGENT_PLATFORM_BASE}/agents`,
    icon: 'agents',
    description: 'Prompts e playground',
  },
  {
    label: 'Fluxos de Trabalho',
    path: `${AGENT_PLATFORM_BASE}/flows`,
    icon: 'flows',
    description: 'Editor visual de automação',
  },
  {
    label: 'Ferramentas',
    path: `${AGENT_PLATFORM_BASE}/tools`,
    icon: 'tools',
    description: 'Tools HTTP, KB, automação',
  },
  {
    label: 'Base de Conhecimento',
    path: `${AGENT_PLATFORM_BASE}/knowledge-bases`,
    icon: 'knowledgeBase',
    description: 'Knowledge Bases (KB)',
  },
  {
    label: 'Execuções',
    path: `${AGENT_PLATFORM_BASE}/executions`,
    icon: 'executions',
    description: 'Histórico e traces',
  },
  {
    label: 'Instâncias de Automação',
    path: `${AGENT_PLATFORM_BASE}/automation-instances`,
    icon: 'automationInstance',
    description: 'Runtimes de automação',
  },
  {
    label: 'Monitoria',
    path: `${AGENT_PLATFORM_BASE}/monitoring`,
    icon: 'monitoring',
    description: 'Métricas e alertas',
  },
  {
    label: 'Governança',
    path: `${AGENT_PLATFORM_BASE}/governance`,
    icon: 'governance',
    description: 'Políticas e auditoria',
  },
];

function isActive(pathname: string, path: string): boolean {
  if (path === AGENT_PLATFORM_BASE) {
    return pathname === AGENT_PLATFORM_BASE;
  }
  return pathname === path || pathname.startsWith(`${path}/`);
}

export const AgentPlatformSidebar: React.FC = () => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`h-screen bg-stone-50 shadow-md transition-all duration-200 flex flex-col ${
        collapsed ? 'w-20' : 'w-64'
      } fixed md:static z-40 border-r border-stone-200`}
    >
      <div className="p-4 flex items-center justify-between border-b border-stone-200">
        {!collapsed && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Contexto</p>
            <p className="text-sm font-semibold text-stone-800">Plataforma Agêntica</p>
          </div>
        )}
        <button
          type="button"
          className="md:block hidden p-2 rounded hover:bg-slate-100 focus:outline-none"
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {collapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            )}
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-1 px-2">
          {menuItems.map(item => {
            const active = isActive(location.pathname, item.path);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center px-3 py-2.5 rounded-lg transition-colors ${
                    active
                      ? 'bg-[#c0392b] text-white shadow-sm'
                      : 'text-stone-700 hover:bg-red-50 hover:text-red-900'
                  } ${collapsed ? 'justify-center' : ''}`}
                  title={collapsed ? item.label : undefined}
                >
                  <span
                    className={`shrink-0 ${collapsed ? '' : 'mr-3'} ${
                      active ? 'text-white' : 'text-[#c0392b]'
                    }`}
                  >
                    <AgentPlatformMenuIcon icon={item.icon} className="h-5 w-5" />
                  </span>
                  {!collapsed && (
                    <div className="min-w-0">
                      <span className="block text-sm font-medium truncate">{item.label}</span>
                      {item.description && (
                        <span className={`block text-xs truncate ${active ? 'text-red-100' : 'text-stone-400'}`}>
                          {item.description}
                        </span>
                      )}
                    </div>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};
